import { Dialog, DialogContent, Grid, makeStyles } from '@material-ui/core';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { approveEditorialContent, approveNodeChanges, rejectEditorialContent, rejectNodeChanges } from '../../api/node';
import { EditorialType, TitleTree, Node, NodeStatus } from '../../domain/domain';
import { ComponentManagementActionType } from '../../reducers/comp.actions';
import { UiActionType } from '../../reducers/ui.reducer';
import EditorPanel from './EditorPanel';
import TreePanel from './TreePanel';
import { ApplicationState } from '../../reducers';
import { getTitleTreeByNumber } from '../../api/title';
import HourglassSpinner from '../HourglassSpinner';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';

const useStyles = makeStyles(theme => ({
    root: {
        padding: 16,
    },
}));

const ComponentManagementPage: React.FC = () => {
    const classes = useStyles();
    const [selectedNodeId, setSelectedNodeId] = useState<number | undefined>(undefined);
    const dispatch = useDispatch();
    const treeRoot = useSelector((app: ApplicationState) => app.comp.treeRoot);
    const [loading, setLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState<string>("Operation in progress...");

    useDocumentTitle("Provision Management");


    function onApproveSubTree(subTree: TitleTree) {
        setLoading(true);
        setLoadingMessage("Approval in progress...");

        // Make a single API call to approve the entire subtree
        approveNodeChanges(subTree.nodeId, true)
            .then(() => {
                // Refresh the Tree view to sync the UI with the updated server state
                if (treeRoot) {
                    getTitleTreeByNumber(treeRoot.citation).then(response => {
                        dispatch({ type: ComponentManagementActionType.TREE_ROOT_UPDATED, treeRoot: response.data });
                    }).catch(error => {
                        console.error(error);
                    })
                }

                // Show success notification before reloading the page
                dispatch({
                    type: UiActionType.SNACKBAR_STATE_CHANGED,
                    newState: { open: true, message: "Subtree Changes Approved.", severity: "success" },
                });
            })
            .catch(error => {
                // Handle and report any errors
                console.error(error);
                dispatch({
                    type: UiActionType.SNACKBAR_STATE_CHANGED,
                    newState: { open: true, message: "Could not Approve Node/s", severity: "error" },
                });
            })
            .finally(() => {
                setLoading(false);
            });
    }

    function onRejectSubTree(subTree: TitleTree) {
        setLoading(true);
        setLoadingMessage("Rejection in progress...");

        // Make a single API call to reject the entire subtree
        rejectNodeChanges(subTree.nodeId, true)
            .then(() => {
                // Refresh the Tree view to sync the UI with the updated server state
                if (treeRoot) {
                    getTitleTreeByNumber(treeRoot.citation).then(response => {
                        dispatch({ type: ComponentManagementActionType.TREE_ROOT_UPDATED, treeRoot: response.data });
                    }).catch(error => {
                        console.error(error);
                    })
                }

                // Show success notification before reloading the page
                dispatch({
                    type: UiActionType.SNACKBAR_STATE_CHANGED,
                    newState: { open: true, message: "Subtree Changes Rejected.", severity: "success" },
                });
            })
            .catch(error => {
                // Handle and report any errors
                console.error(error);
                dispatch({
                    type: UiActionType.SNACKBAR_STATE_CHANGED,
                    newState: { open: true, message: "Could not Reject Node/s", severity: "error" },
                });
            })
            .finally(() => {
                setLoading(false);
            });
    }

    function onRejectNode(nodeId: number) {
        rejectNodeChanges(nodeId, false).then(response => {
            let hasChanges = response.data.hasUnapprovedEditorialContent || response.data.status === NodeStatus.CHANGED_NOT_APPROVED;
            dispatch({
                type: ComponentManagementActionType.SUBTREE_TITLE_CHANGED,
                nodeId: nodeId,
                newHeading: response.data.heading,
                newWorkingCopyHeading: response.data.nodeHistory?.heading,
                newCitation: response.data.citation,
                newWorkingCopyCitation: response.data?.nodeHistory?.citation,
                repealed: response.data.repealed,
                newCitationPart: response.data.citationPart
            })
            dispatch({ type: ComponentManagementActionType.SUBTREE_CHANGED, nodeId: nodeId, hasChanges: hasChanges })
            dispatch({ type: UiActionType.SNACKBAR_STATE_CHANGED, newState: { open: true, message: "Changes Rejected.", severity: "success" } });
            dispatch({ type: ComponentManagementActionType.EDITOR_NODE_UPDATED, editorNode: response.data });
            dispatch({
                type: ComponentManagementActionType.HEADING_CHANGED,
                nodeId: nodeId,
                headingHasChanges: false,
            })
            dispatch({
                type: ComponentManagementActionType.EDITORIAL_CONTENT_CHANGED,
                nodeId: nodeId,
                editorialContentHasChanges: false,
            })
            dispatch({
                type: ComponentManagementActionType.STATUTORY_CONTENT_CHANGED,
                nodeId: nodeId,
                statutoryContentHasChanges: false,
            })
        }).catch(error => {
            console.error(error);
            dispatch({ type: UiActionType.SNACKBAR_STATE_CHANGED, newState: { open: true, message: "Could not Reject Node", severity: "error" } });
        })
    }

    function onApproveNode(nodeId: number) {
        approveNodeChanges(nodeId, false).then(response => {
            let hasChanges = response.data.hasUnapprovedEditorialContent || response.data.status === NodeStatus.CHANGED_NOT_APPROVED;

            dispatch({
                type: ComponentManagementActionType.SUBTREE_TITLE_CHANGED,
                nodeId: nodeId,
                newHeading: response.data.heading,
                newWorkingCopyHeading: response.data.nodeHistory?.heading,
                newCitation: response.data.citation,
                newWorkingCopyCitation: response.data?.nodeHistory?.citation,
                repealed: response.data.repealed,
                newCitationPart: response.data.citationPart
            })
            dispatch({ type: ComponentManagementActionType.SUBTREE_CHANGED, nodeId: nodeId, hasChanges: hasChanges })
            dispatch({ type: UiActionType.SNACKBAR_STATE_CHANGED, newState: { open: true, message: "Changes Approved.", severity: "success" } });
            dispatch({ type: ComponentManagementActionType.EDITOR_NODE_UPDATED, editorNode: response.data });

            dispatch({
                type: ComponentManagementActionType.HEADING_CHANGED,
                nodeId: nodeId,
                headingHasChanges: false,
            })
            dispatch({
                type: ComponentManagementActionType.EDITORIAL_CONTENT_CHANGED,
                nodeId: nodeId,
                editorialContentHasChanges: false,
            })
            dispatch({
                type: ComponentManagementActionType.STATUTORY_CONTENT_CHANGED,
                nodeId: nodeId,
                statutoryContentHasChanges: false,
            })
        }).catch(error => {
            console.error(error);
            dispatch({ type: UiActionType.SNACKBAR_STATE_CHANGED, newState: { open: true, message: "Could not Approve Node", severity: "error" } });
        })
    }

    function onApproveEditorialContent(node: Node, editorialType: EditorialType) {
        approveEditorialContent(node, editorialType).then(response => {
            let hasChanges = response.data.hasUnapprovedEditorialContent || response.data.status === NodeStatus.CHANGED_NOT_APPROVED;
            dispatch({ type: ComponentManagementActionType.SUBTREE_CHANGED, nodeId: node.nodeId, hasChanges: hasChanges })
            dispatch({ type: ComponentManagementActionType.EDITOR_NODE_UPDATED, editorNode: response.data });
        }).catch(error => {
            console.error(error);
        })
        dispatch({ type: UiActionType.SNACKBAR_STATE_CHANGED, newState: { open: true, message: "Changes Approved.", severity: "success" } });
    }

    function onRejectEditorialContent(node: Node, editorialType: EditorialType) {
        rejectEditorialContent(node, editorialType).then(response => {
            let hasChanges = response.data.hasUnapprovedEditorialContent || response.data.status === NodeStatus.CHANGED_NOT_APPROVED;
            dispatch({ type: ComponentManagementActionType.SUBTREE_CHANGED, nodeId: node.nodeId, hasChanges: hasChanges })
            dispatch({ type: ComponentManagementActionType.EDITOR_NODE_UPDATED, editorNode: response.data });
        }).catch(error => {
            console.error(error);
        })

        dispatch({ type: UiActionType.SNACKBAR_STATE_CHANGED, newState: { open: true, message: "Changes Rejected.", severity: "success" } });
    }

    return (
        <Grid container className={`${classes.root} flex-gap-2`} direction='row' wrap='nowrap'>
            {loading && (
                <Dialog open={loading}>
                    <DialogContent>
                        <HourglassSpinner message={loadingMessage} />
                    </DialogContent>
                </Dialog>
            )}
            <Grid item xs={3}>
                <TreePanel selectedNodeId={selectedNodeId}
                    onApproveSubTree={onApproveSubTree}
                    onRejectSubTree={onRejectSubTree}
                    onSelectedNodeChanged={(newNodeId) => { setSelectedNodeId(newNodeId); }
                    } />
            </Grid>
            <Grid item xs={9}>
                <EditorPanel nodeId={selectedNodeId}
                    onRejectNode={(node) => onRejectNode(node.nodeId)}
                    onApproveNode={(node) => onApproveNode(node.nodeId)}
                    onApproveEditorialContent={onApproveEditorialContent}
                    onRejectEditorialContent={onRejectEditorialContent}
                />
            </Grid>
        </Grid>
    )

}

export default ComponentManagementPage;
